import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { relativeTime } from '../../utils/dateUtils';
import '../shared.css';
import './InstancesPage.css';

const POLL_MS = 15_000;

interface InstanceRow {
  kg_id: string;
  kg_name: string;
  version_id: string;
  instance_id: string;
  instance_type: string;
  status: string;
  created_at: number;
  default_instance: boolean;
  is_terminated: boolean;
  created_by?: string;
  created_by_email?: string;
}

function statusPillMod(status: string): string {
  switch (status?.toUpperCase()) {
    case 'RUNNING':
    case 'ACTIVE':       return 'status-pill--running';
    case 'STOPPED':      return 'status-pill--stopped';
    case 'TERMINATED':   return 'status-pill--terminated';
    case 'PROVISIONING':
    case 'PENDING':      return 'status-pill--provisioning';
    case 'STOPPING':     return 'status-pill--stopping';
    case 'ERROR':        return 'status-pill--error';
    default:             return 'status-pill--stopped';
  }
}

function secondsSince(d: Date) { return Math.floor((Date.now() - d.getTime()) / 1000); }

const InstancesPage: React.FC = () => {
  const [instances, setInstances] = useState<InstanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'running' | 'stopped'>('all');
  const [lastPolled, setLastPolled] = useState<Date | null>(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [terminatingId, setTerminatingId] = useState<string|null>(null);
  const [stoppingId, setStoppingId] = useState<string|null>(null);
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!lastPolled) return;
    const t = setInterval(() => setSecondsAgo(secondsSince(lastPolled)), 1000);
    return () => clearInterval(t);
  }, [lastPolled]);

  const load = useCallback(async (showSpinner = true) => {
    try {
      if (showSpinner) setLoading(true);
      setError('');
      const res: any = await apiService.getAllInstances();
      if (res.data) {
        setInstances(res.data.map((item: any) => {
          const a = item.attributes ?? item;
          return {
            kg_id:            a.kg_id          ?? item.kg_id          ?? '',
            kg_name:          a.kg_name        || item.kg_name        || a.kg_id || item.kg_id || 'Unknown',
            version_id:       String(a.version_id  ?? item.version_id  ?? ''),
            instance_id:      a.instance_id    ?? item.id             ?? '',
            instance_type:    a.instance_type  ?? item.instance_type  ?? 'instance',
            status:           a.status         ?? item.status         ?? 'unknown',
            created_at:       a.created_at     ?? item.created_at,
            default_instance: a.default_instance ?? item.default_instance ?? false,
            is_terminated:    a.is_terminated   ?? item.is_terminated  ?? false,
            created_by_email: a.created_by_email ?? item.created_by_email ?? '',
            created_by:       a.created_by       ?? item.created_by       ?? '',
          };
        }));
        setLastPolled(new Date());
        setSecondsAgo(0);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load instances'));
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    timerRef.current = setInterval(() => load(false), POLL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [load]);

  const copyId = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id).then(() => {
      setCopyToast(id);
      setTimeout(() => setCopyToast(null), 1800);
    });
  };

  const handleTerminate = async (inst: InstanceRow) => {
    try {
      setTerminatingId(inst.instance_id);
      await apiService.updateInstanceStatus(inst.kg_id, inst.version_id, inst.instance_id, 'TERMINATED');
      setTimeout(() => load(false), 1500);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to terminate instance'));
    } finally {
      setTerminatingId(null);
    }
  };

  const handleStop = async (inst: InstanceRow) => {
    try {
      setStoppingId(inst.instance_id);
      await apiService.updateInstanceStatus(inst.kg_id, inst.version_id, inst.instance_id, 'STOPPED');
      setTimeout(() => load(false), 1500);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to stop instance'));
    } finally {
      setStoppingId(null);
    }
  };

  const filtered = instances.filter(i => {
    const q = searchTerm.toLowerCase();
    const matchSearch = !q ||
      i.kg_name.toLowerCase().includes(q) ||
      i.kg_id.toLowerCase().includes(q) ||
      i.instance_id.toLowerCase().includes(q) ||
      i.instance_type.toLowerCase().includes(q) ||
      i.status.toLowerCase().includes(q);
    const matchStatus =
      statusFilter === 'all' ||
      i.status.toLowerCase() === statusFilter ||
      (statusFilter === 'running' && ['running', 'active'].includes(i.status.toLowerCase()));
    return matchSearch && matchStatus;
  });

  const runningCount = instances.filter(i =>
    ['running', 'active'].includes(i.status.toLowerCase()) && !i.is_terminated
  ).length;
  const stoppedCount = instances.filter(i => i.status.toLowerCase() === 'stopped').length;

  return (
    <div className="instances-page">

      {/* ── Summary strip ── */}
      {!loading && (
        <div className="ip-summary-strip">
          <button
            className={`ip-strip-stat${statusFilter === 'all' ? ' ip-strip-stat--active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            <span className="ip-strip-val">{instances.length}</span>
            <span className="ip-strip-label">Total Instances</span>
          </button>
          <div className="ip-strip-divider" />
          <button
            className={`ip-strip-stat${statusFilter === 'running' ? ' ip-strip-stat--active' : ''}`}
            onClick={() => setStatusFilter('running')}
          >
            <span className="ip-strip-val ip-strip-val--running">{runningCount}</span>
            <span className="ip-strip-label">Running</span>
          </button>
          <div className="ip-strip-divider" />
          <button
            className={`ip-strip-stat${statusFilter === 'stopped' ? ' ip-strip-stat--active' : ''}`}
            onClick={() => setStatusFilter('stopped')}
          >
            <span className="ip-strip-val">{stoppedCount}</span>
            <span className="ip-strip-label">Stopped</span>
          </button>
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="ip-toolbar">
        <div className="ip-toolbar-left">
          <input
            className="tab-search"
            placeholder="Search by KG, instance ID, type, status…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {!loading && (
            <span className="tab-stat-pill">{filtered.length} of {instances.length}</span>
          )}
          {lastPolled && (
            <span className="ip-poll-label">
              Updated {secondsAgo < 5 ? 'just now' : `${secondsAgo}s ago`}
            </span>
          )}
        </div>
        <div className="ip-toolbar-right">
          <button
            className="tab-btn tab-btn--ghost ip-refresh-btn"
            onClick={() => load(false)}
            title="Refresh"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="tab-banner tab-banner--error">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">{error}</span>
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div className="tab-loader"><span className="tab-spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="tab-empty">
          <div className="tab-empty-icon">⬡</div>
          <div className="tab-empty-title">
            {instances.length === 0 ? 'No instances found' : 'No matching instances'}
          </div>
          <div className="tab-empty-body">
            {instances.length === 0
              ? 'Launch an instance from a KG version to see it here.'
              : 'Try adjusting your search or filter.'}
          </div>
          {statusFilter !== 'all' && (
            <button className="tab-btn tab-btn--ghost" style={{ marginTop: 8 }}
              onClick={() => setStatusFilter('all')}>
              Show all
            </button>
          )}
        </div>
      ) : (
        <div className="ip-table-wrapper sticky-thead">
          <table className="ip-table">
            <thead>
              <tr>
                <th>Knowledge Graph</th>
                <th>Version</th>
                <th>Instance ID</th>
                <th>Type</th>
                <th>Status</th>
                <th>Created</th>
                <th>Created By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inst) => {
                const rt = relativeTime(inst.created_at);
                return (
                  <tr
                    key={inst.instance_id}
                    className="ip-row"
                    onClick={() => navigate(`/kg/${inst.kg_id}/version/${inst.version_id}`)}
                    title={`Go to version ${inst.version_id}`}
                  >
                    <td className="ip-kg-cell">
                      <div className="kg-name-primary">{inst.kg_name !== inst.kg_id ? inst.kg_name : inst.kg_id}</div>
                      {inst.kg_name !== inst.kg_id && (
                        <div className="kg-name-id mono-sm copy-cell" onClick={(e) => copyId(inst.kg_id, e)} title="Click to copy ID">
                          {inst.kg_id}
                          <span className="copy-hint">{copyToast === inst.kg_id ? '✓ copied' : 'copy'}</span>
                        </div>
                      )}
                    </td>
                    <td className="mono-sm">v{inst.version_id}</td>
                    <td
                      className="ip-inst-id copy-cell"
                      onClick={(e) => copyId(inst.instance_id, e)}
                      title="Click to copy instance ID"
                    >
                      <span className="mono-sm">{inst.instance_id}</span>
                      {inst.default_instance && (
                        <span className="ip-default-tag">default</span>
                      )}
                      <span className="copy-hint">{copyToast === inst.instance_id ? '✓ copied' : 'copy'}</span>
                    </td>
                    <td>
                      <span className="ip-type-tag">{inst.instance_type}</span>
                    </td>
                    <td>
                      <span className={`status-pill ${statusPillMod(inst.status)}`}>
                        {inst.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="ip-muted ip-date" title={rt.absolute}>{rt.relative}</td>
                    <td className="ip-muted ip-created-by">
                      {inst.created_by_email || inst.created_by || <span style={{opacity:0.4}}>—</span>}
                    </td>
                    <td className="ip-actions-cell" onClick={e => e.stopPropagation()}>
                      {inst.status?.toLowerCase() === 'running' && !inst.is_terminated && (
                        <>
                          <button
                            className="ip-action-btn ip-action-btn--warn"
                            disabled={stoppingId === inst.instance_id}
                            onClick={() => handleStop(inst)}
                          >
                            Stop
                          </button>
                          <button
                            className="ip-action-btn ip-action-btn--danger"
                            disabled={terminatingId === inst.instance_id}
                            onClick={() => handleTerminate(inst)}
                          >
                            Terminate
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
};

export default InstancesPage;
